/**
 * One announcement's editor (container, phase 7b item 5), or a new one's
 * (`/vendor/announcements/new`).
 *
 * Live and dirty-aware (D35, `useLiveResource`, as the studio editor):
 * another vendor's save refreshes the page in place while it has no unsaved
 * edits; with unsaved edits nothing is replaced and the banner offers
 * "Overwrite with mine" or "Discard mine and load theirs" — as does a save
 * that answers 409. A delete by someone else is a banner, the edits kept.
 */
import type { Announcement } from '@acp/api-contract/vendor.js';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AnnouncementConflict } from '../api/vendor.js';
import { type LiveResource, useCreateAnnouncement, useDeleteAnnouncement, useLiveResource, useNow, useSaveAnnouncement, useServices, useVendorAnnouncement, useVendorTenants } from '../app/hooks.js';
import { LiveUpdateBanner } from '../components/LiveUpdateBanner.js';
import { Button } from '../components/ui/button.js';
import { AnnouncementEditor } from '../components/vendor/AnnouncementEditor.js';
import { announcementHref } from '../components/vendor/AnnouncementsList.js';
import { type AnnouncementDraft, blankDraft, draftOf, editOf, endNowInput, isDraftDirty, judgeDraft } from '../lib/announcementDraft.js';
import { whenText } from '../lib/live/whenText.js';
import { NEW_ANNOUNCEMENT } from './VendorAnnouncementsView.js';
import { useVendorAdmin } from './VendorLayout.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function useTenantChoices() {
  const tenants = useVendorTenants();
  return (tenants.data ?? []).map((t) => ({ tenantId: t.tenantId, name: t.name }));
}

export function VendorAnnouncementView({ announcementId }: { readonly announcementId: string }) {
  if (announcementId === NEW_ANNOUNCEMENT) return <NewAnnouncementPage />;
  return <ExistingAnnouncement announcementId={announcementId} />;
}

function ExistingAnnouncement({ announcementId }: { readonly announcementId: string }) {
  const query = useVendorAnnouncement(announcementId);
  // Data first: a background refetch that fails must not replace the editor (and the user's unsaved edits).
  if (query.data !== undefined) return <AnnouncementEditorPage key={query.data.announcementId} latest={query.data} />;
  if (query.error !== null) {
    return (
      <div className="grid gap-4" role="alert" data-testid="announcement-failed">
        <p className="text-sm text-destructive">Could not load the announcement: {messageOf(query.error)}</p>
        <Button variant="outline" className="w-fit" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  return <p className="p-8 text-center text-sm text-muted-foreground">Loading the announcement…</p>;
}

function NewAnnouncementPage() {
  const { nowMs } = useServices();
  const tenants = useTenantChoices();
  const canAdmin = useVendorAdmin();
  const create = useCreateAnnouncement();
  const navigate = useNavigate();
  const [initial] = useState(() => blankDraft(nowMs()));
  const [draft, setDraft] = useState<AnnouncementDraft>(initial);
  const judged = useMemo(() => judgeDraft(draft), [draft]);
  return (
    <AnnouncementEditor
      mode="create"
      draft={draft}
      tenants={tenants}
      problems={judged.ok ? (create.error === null ? [] : [messageOf(create.error)]) : judged.problems}
      version={null}
      updatedBy={null}
      dirty={JSON.stringify(draft) !== JSON.stringify(initial)}
      canSave={canAdmin}
      pending={create.isPending}
      onDraft={(next) => {
        create.reset();
        setDraft(next);
      }}
      onSave={() => {
        if (!judged.ok) return;
        create.mutate(judged.request, {
          onSuccess: (made) => {
            toast.success(`Published “${made.title}”`);
            void navigate({ to: announcementHref(made.announcementId) });
          },
        });
      }}
      onRevert={() => setDraft(initial)}
      onEndNow={() => undefined}
      onDelete={() => undefined}
    />
  );
}

/** The draft's save (a 409 goes to the live banner) and what a failed one says. */
function useAnnouncementSave(live: LiveResource<Announcement>) {
  const save = useSaveAnnouncement();
  const [failure, setFailure] = useState<string | null>(null);
  const submit = (draft: AnnouncementDraft, version: number): void => {
    const judged = judgeDraft(draft);
    if (!judged.ok) return;
    setFailure(null);
    live.beginSave();
    save.mutate(
      { announcementId: live.base.announcementId, edit: editOf(judged.request, { ...live.base, version }) },
      {
        onSuccess: (saved) => {
          live.saved(saved);
          toast.success(`Saved version ${String(saved.version)}`);
        },
        onError: (err) => {
          if (err instanceof AnnouncementConflict) live.conflict(err.current);
          else {
            live.saveFailed();
            setFailure(messageOf(err));
          }
        },
      },
    );
  };
  return { submit, pending: save.isPending, failure, clearFailure: () => setFailure(null) };
}

function AnnouncementEditorPage({ latest }: { readonly latest: Announcement }) {
  const tenants = useTenantChoices();
  const canAdmin = useVendorAdmin();
  const now = useNow(60_000);
  const { nowMs } = useServices();
  const navigate = useNavigate();
  const remove = useDeleteAnnouncement();
  const [loaded] = useState(latest);
  const [draft, setDraft] = useState<AnnouncementDraft>(() => draftOf(loaded));
  const live = useLiveResource<Announcement>({
    kind: 'announcement',
    id: loaded.announcementId,
    loaded,
    latest,
    isDirty: (base) => isDraftDirty(draft, base),
    versionOf: (a) => a.version,
    authorOf: (a) => a.updatedBy,
    writtenAtOf: (a) => Date.parse(a.updatedAt),
    onReset: (a) => setDraft(draftOf(a)),
  });
  const saving = useAnnouncementSave(live);
  const judged = useMemo(() => judgeDraft(draft), [draft]);
  const dirty = isDraftDirty(draft, live.base);
  const notice = live.notice;
  const problems = judged.ok ? (saving.failure === null ? [] : [saving.failure]) : judged.problems;

  return (
    <div className="grid gap-4" data-testid="announcement-page">
      {notice === null ? null : (
        <LiveUpdateBanner
          notice={{ kind: notice.kind, who: notice.by, at: whenText(notice.atMs, now), what: 'announcement' }}
          pending={saving.pending}
          canSave={canAdmin && judged.ok}
          onOverwrite={() => {
            const theirs = live.keepMine();
            if (theirs !== null) saving.submit(draft, theirs.version);
          }}
          onDiscard={() => {
            saving.clearFailure();
            live.discardMine();
          }}
        />
      )}
      <div className="relative" data-refreshes={live.refreshes}>
        {live.refreshes > 0 ? <span key={live.refreshes} aria-hidden="true" className="pointer-events-none absolute inset-0 animate-live-flash rounded-xl ring-2 ring-success" data-testid="live-flash" /> : null}
        <AnnouncementEditor
          mode="edit"
          draft={draft}
          tenants={tenants}
          problems={problems}
          version={live.base.version}
          updatedBy={live.base.updatedBy}
          dirty={dirty}
          canSave={canAdmin}
          pending={saving.pending || remove.isPending}
          onDraft={(next) => {
            saving.clearFailure();
            setDraft(next);
          }}
          onSave={() => saving.submit(draft, live.base.version)}
          onRevert={() => {
            saving.clearFailure();
            setDraft(draftOf(live.base));
          }}
          onEndNow={() => {
            // Ends it now: the draft's end becomes now (the viewer saves it, as any other edit).
            saving.clearFailure();
            setDraft({ ...draft, endsAt: endNowInput(nowMs()) });
          }}
          onDelete={() =>
            remove.mutate(live.base.announcementId, {
              onSuccess: () => {
                toast.success('Deleted');
                void navigate({ to: '/vendor/announcements' });
              },
              onError: (err) => toast.error(`Not deleted: ${messageOf(err)}`),
            })
          }
        />
      </div>
    </div>
  );
}
